/**
 * SSR-safe date formatting utilities.
 * Uses explicit French month names to avoid locale-dependent toLocaleDateString()
 * which can cause hydration mismatches between server and client.
 */

const MONTHS_SHORT_FR = [
    'janv', 'févr', 'mars', 'avr', 'mai', 'juin',
    'juil', 'août', 'sept', 'oct', 'nov', 'déc'
]

const MONTHS_FULL_FR = [
    'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
    'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'
]

export const useDateFormat = () => {
    /**
     * Format date as "05 janv" (day + short month)
     */
    const formatDayMonth = (dateInput: string | Date): string => {
        const d = typeof dateInput === 'string' ? new Date(dateInput) : dateInput
        const day = d.getDate().toString().padStart(2, '0')
        const month = MONTHS_SHORT_FR[d.getMonth()] || ''
        return `${day} ${month}`
    }

    /**
     * Format date as "05 janv 2026" (day + short month + year)
     */
    const formatDayMonthYear = (dateInput: string | Date): string => {
        const d = typeof dateInput === 'string' ? new Date(dateInput) : dateInput
        const day = d.getDate().toString().padStart(2, '0')
        const month = MONTHS_SHORT_FR[d.getMonth()] || ''
        const year = d.getFullYear()
        return `${day} ${month} ${year}`
    }

    /**
     * Get separate day and month for display purposes
     */
    const getDateParts = (dateInput: string | Date): { day: string; month: string; displayDate: string } => {
        const d = typeof dateInput === 'string' ? new Date(dateInput) : dateInput
        const day = d.getDate().toString()
        const month = MONTHS_SHORT_FR[d.getMonth()] || ''
        return {
            day,
            month,
            displayDate: `${day.padStart(2, '0')} ${month}`
        }
    }

    /**
     * Get year from date
     */
    const getYear = (dateInput: string | Date): number => {
        const d = typeof dateInput === 'string' ? new Date(dateInput) : dateInput
        return d.getFullYear()
    }

    /**
     * Format date as full French format "5 janvier 2026"
     */
    const formatFullDate = (dateInput: string | Date): string => {
        const d = typeof dateInput === 'string' ? new Date(dateInput) : dateInput
        const day = d.getDate()
        const month = MONTHS_FULL_FR[d.getMonth()] || ''
        const year = d.getFullYear()
        return `${day} ${month} ${year}`
    }

    /**
     * Format date as "05/01/2026" (numeric format)
     */
    const formatNumericDate = (dateInput: string | Date): string => {
        const d = typeof dateInput === 'string' ? new Date(dateInput) : dateInput
        const day = d.getDate().toString().padStart(2, '0')
        const month = (d.getMonth() + 1).toString().padStart(2, '0')
        const year = d.getFullYear()
        return `${day}/${month}/${year}`
    }

    return {
        formatDayMonth,
        formatDayMonthYear,
        getDateParts,
        getYear,
        formatFullDate,
        formatNumericDate
    }
}
